import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { UserAvatar } from '../components/shared/UserAvatar';
import { SavedPlacesSection } from '../components/profile/SavedPlacesSection';
import {
  MapPin,
  CalendarDays,
  BarChart3,
  Store,
  CheckCircle2,
} from 'lucide-react';

interface VisitStats {
  visits_today: number;
  visits_last_7_days: number;
  visits_this_month: number;
  visits_total: number;
  placements_secured_this_month: number;
  placements_secured_total: number;
}

const emptyStats: VisitStats = {
  visits_today: 0,
  visits_last_7_days: 0,
  visits_this_month: 0,
  visits_total: 0,
  placements_secured_this_month: 0,
  placements_secured_total: 0,
};

/** Mirrors the fallback in the backend's buildSignature. */
const DEFAULT_SIGNATURE_NAME = 'Valentino Faldon';
const NON_CUSTOMER_LOGIN_EMAILS = new Set(['admin@brandcircuit.de']);

function resolvePreviewEmail(signatureEmail: string, loginEmail: string): string | null {
  const dedicated = signatureEmail.trim();
  if (dedicated) return dedicated;
  const login = loginEmail.trim();
  if (!login) return null;
  if (NON_CUSTOMER_LOGIN_EMAILS.has(login.toLowerCase())) return null;
  return login;
}

/**
 * Live preview of the signature block used in follow-up emails to a PoS. It applies the
 * same fallback as the backend so an incomplete profile is visible here rather than in a
 * mail that already reached a customer.
 */
const SignaturePreview: React.FC<{
  fullName: string;
  jobTitle: string;
  phone: string;
  signatureEmail: string;
  loginEmail: string;
  extra: string;
}> = ({ fullName, jobTitle, phone, signatureEmail, loginEmail, extra }) => {
  const name = fullName.trim();
  const email = resolvePreviewEmail(signatureEmail, loginEmail);
  const usesDefault = !name || !email;
  const missing = [
    !jobTitle.trim() && 'job title',
    !phone.trim() && 'phone number',
    !signatureEmail.trim() && 'signature email',
  ].filter(Boolean) as string[];

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-sm font-medium text-gray-700 mb-2">
        Signature in follow-up emails
      </p>
      {usesDefault ? (
        <p className="text-sm text-amber-700">
          Without a full name and signature email your emails are signed with the company
          signature ({DEFAULT_SIGNATURE_NAME} · mail@brandcircuit.de). Add both to sign as
          yourself.
        </p>
      ) : (
        <div className="text-sm text-gray-800 space-y-0.5">
          <p className="font-semibold">{name}</p>
          {jobTitle.trim() && <p className="text-gray-600">{jobTitle.trim()}</p>}
          <p className="text-gray-600">Brandcircuit UG</p>
          <p className="text-gray-600">Florastraße 34a, 13187 Berlin</p>
          {phone.trim() && <p className="text-gray-600">Tel. {phone.trim()}</p>}
          <p className="text-gray-600">E-Mail {email}</p>
          <p className="text-gray-600">Web www.brandcircuit.de</p>
          {extra.trim() && <p className="text-gray-500 pt-1">{extra.trim()}</p>}
        </div>
      )}
      {!usesDefault && missing.length > 0 && (
        <p className="text-xs text-amber-700 mt-2">
          Add your {missing.join(' and ')} so customers can reach you directly.
        </p>
      )}
    </div>
  );
};

const Profile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [fullName, setFullName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [bio, setBio] = useState('');
  const [signatureEmail, setSignatureEmail] = useState('');
  const [signatureExtra, setSignatureExtra] = useState('');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [stats, setStats] = useState<VisitStats>(emptyStats);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    if (!user) return;
    setFullName(user.fullName || '');
    setJobTitle(user.jobTitle || '');
    setPhone(user.phone || '');
    setCity(user.city || '');
    setBio(user.bio || '');
    setSignatureEmail(user.signatureEmail || '');
    setSignatureExtra(user.emailSignatureExtra || '');
    setPhotoUrl(user.photoUrl || null);
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    const load = async () => {
      setLoadingStats(true);
      try {
        const res = await api.get(`/visits/stats?user_id=${user.id}`);
        setStats({ ...emptyStats, ...(res.data || {}) });
      } catch {
        toast.error('Failed to load personal stats');
      } finally {
        setLoadingStats(false);
      }
    };
    load();
  }, [user?.id]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.patch('/users/me', {
        full_name: fullName.trim(),
        job_title: jobTitle.trim(),
        phone: phone.trim(),
        city: city.trim(),
        bio: bio.trim(),
        signature_email: signatureEmail.trim(),
        email_signature_extra: signatureExtra.trim(),
      });
      setPhotoUrl(res.data.photoUrl || null);
      await refreshUser();
      toast.success('Profile saved');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/users/me/photo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        transformRequest: [
          (data, headers) => {
            // Let the browser set multipart boundary
            if (headers && typeof headers === 'object') {
              delete (headers as Record<string, unknown>)['Content-Type'];
            }
            return data;
          },
        ],
      });
      setPhotoUrl(res.data.photoUrl || null);
      await refreshUser();
      toast.success('Photo updated');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to upload photo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    setUploading(true);
    try {
      const res = await api.delete('/users/me/photo');
      setPhotoUrl(res.data.photoUrl || null);
      await refreshUser();
      toast.success('Photo removed');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to remove photo');
    } finally {
      setUploading(false);
    }
  };

  if (!user) return null;

  const display = (n: number) => (loadingStats ? '...' : n);

  const StatCard = ({
    title,
    value,
    icon: Icon,
  }: {
    title: string;
    value: number | string;
    icon: React.ElementType;
  }) => (
    <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
      <div className="flex items-center gap-2 mb-2 text-brand-600">
        <Icon size={18} />
        <span className="text-xs font-medium text-gray-500">{title}</span>
      </div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
        <p className="text-gray-500 text-sm">
          Your photo and details are visible to the team on the leaderboard.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 space-y-6">
        <div className="flex items-center gap-4">
          <UserAvatar
            userId={user.id}
            fullName={fullName || user.fullName}
            photoUrl={photoUrl}
            size="lg"
          />
          <div className="space-y-2">
            <label className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 cursor-pointer">
              {uploading ? 'Uploading…' : 'Upload photo'}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                disabled={uploading}
                onChange={handlePhotoChange}
              />
            </label>
            {photoUrl && (
              <button
                type="button"
                onClick={handleRemovePhoto}
                disabled={uploading}
                className="block text-sm text-red-600 hover:text-red-700"
              >
                Remove photo
              </button>
            )}
            <p className="text-xs text-gray-500">JPEG, PNG or WebP · max 2 MB</p>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full name
            </label>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job title
            </label>
            <input
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="Field Agent"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 outline-none"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bio
            </label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              maxLength={500}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 outline-none resize-none"
            />
            <p className="text-xs text-gray-400 mt-1">{bio.length}/500</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Signature email (customer emails)
            </label>
            <input
              type="email"
              value={signatureEmail}
              onChange={(e) => setSignatureEmail(e.target.value)}
              placeholder="mail@brandcircuit.de"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 outline-none"
            />
            <p className="text-xs text-gray-400 mt-1">
              Shown in the email signature. Customer replies always go to
              mail@brandcircuit.de. Your login ({user.email}) stays unchanged.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Signature addition (customer emails)
            </label>
            <textarea
              value={signatureExtra}
              onChange={(e) => setSignatureExtra(e.target.value)}
              maxLength={300}
              rows={2}
              placeholder="e.g. Mobil erreichbar Mo–Fr 9–18 Uhr"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 outline-none resize-none"
            />
            <p className="text-xs text-gray-400 mt-1">
              Appended below your name, job title, phone and email in follow-up emails.
              {' '}
              {signatureExtra.length}/300
            </p>
          </div>
          <SignaturePreview
            fullName={fullName}
            jobTitle={jobTitle}
            phone={phone}
            signatureEmail={signatureEmail}
            loginEmail={user.email}
            extra={signatureExtra}
          />
          <button
            type="submit"
            disabled={saving}
            className="px-5 py-2.5 bg-brand-600 text-white rounded-lg font-medium hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </form>
      </div>

      <SavedPlacesSection />

      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          My visit metrics
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard title="Today" value={display(stats.visits_today)} icon={CalendarDays} />
          <StatCard title="Last 7 days" value={display(stats.visits_last_7_days)} icon={MapPin} />
          <StatCard title="This month" value={display(stats.visits_this_month)} icon={BarChart3} />
          <StatCard title="Total visits" value={display(stats.visits_total)} icon={Store} />
          <StatCard
            title="Placements this month"
            value={display(stats.placements_secured_this_month)}
            icon={CheckCircle2}
          />
          <StatCard
            title="Placements total"
            value={display(stats.placements_secured_total)}
            icon={CheckCircle2}
          />
        </div>
      </div>
    </div>
  );
};

export default Profile;
